import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'

import { observer } from 'mobx-react-lite'

import { Close, VisibilityOff } from '@mxenabled/mx-icons'
import { P, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore } from '../../common'

export interface HiddenConfirmationCardProps {
  onClose: () => void
  onUndo: () => void
  onLeaveFeedback: () => void
}

/** Inline confirmation card shown after hiding an insight. */
export const HiddenConfirmationCard = observer(
  ({ onClose, onUndo, onLeaveFeedback }: HiddenConfirmationCardProps) => {
    const copy = useGlobalCopyStore().insights_feed?.hidden_confirmation

    if (!copy) return null

    return (
      <Card
        elevation={1}
        sx={{
          position: 'relative',
          bgcolor: 'action.disabledBackground',
          borderRadius: 2,
          pt: 16,
          px: 16,
          pb: 8,
        }}
      >
        <IconButton
          aria-label={copy.close_aria}
          onClick={onClose}
          sx={{ minHeight: 40, minWidth: 40, position: 'absolute', right: 4, top: 4 }}
        >
          <Close size={20} sx={{ color: 'text.secondary' }} />
        </IconButton>

        <Stack sx={{ gap: 6 }}>
          <Stack sx={{ flexDirection: 'row', gap: 12 }}>
            <Box
              sx={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VisibilityOff size={20} sx={{ color: 'text.secondary' }} />
            </Box>

            <Stack sx={{ gap: 6 }}>
              <P aria-live="assertive" bold sx={{ lineHeight: '16px' }} variant="body1">
                {copy.title}
              </P>

              <P
                sx={{ color: 'text.primary', fontSize: 13, lineHeight: '20px' }}
                variant="subtitle2"
              >
                {copy.description}
              </P>
            </Stack>
          </Stack>

          <Stack
            sx={{
              alignItems: 'center',
              flexDirection: 'row',
              gap: 4,
              height: 28,
              pb: 6,
              pl: 36,
            }}
          >
            <Button onClick={onUndo} size="small" sx={{ minWidth: 0, px: 8 }} variant="text">
              <Text bold variant="body2">
                {copy.undo}
              </Text>
            </Button>
            <Button
              onClick={onLeaveFeedback}
              size="small"
              sx={{ minWidth: 0, px: 8 }}
              variant="text"
            >
              <Text bold sx={{ color: 'text.secondary' }} variant="body2">
                {copy.leave_feedback}
              </Text>
            </Button>
          </Stack>
        </Stack>
      </Card>
    )
  },
)

export default HiddenConfirmationCard
